import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Linking, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { getBaseOs } from 'react-native-device-info';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { AppLogo, AppStoreIcon, GooglePlayIcon } from 'assets/images';

import { ANDROID_APP_ID, IOS_APP_ID, IOS_APP_NAME } from 'config/constants';
import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import { LOGO_TEST_ID } from 'config/testID';

import { selectLayoutConfig } from 'store/config/selectors';

import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import {
    isMobileMediaQuery,
    isTabletMediaQuery,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import ActionButton from 'components/CustomButtons/ActionButton';
import SvgButton from 'components/CustomButtons/SvgButton';
import LocalizedText from 'components/LocalizedText';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

const {
    LANDING_PAGE_MOBILE_BROWSER_TITLE,
    LANDING_PAGE_MOBILE_BROWSER_BODY,
    LANDING_PAGE_MOBILE_BROWSER_STORE_LABEL,
    LANDING_PAGE_MOBILE_BROWSER_OR_LABEL,
    LANDING_PAGE_MOBILE_BROWSER_OPEN_APP_BUTTON,
    ACCESSIBILITY: {
        LANDING: {
            ACCESSIBILITY_LANDING_PAGE_GOOGLE_PLAY_BUTTON,
            ACCESSIBILITY_LANDING_PAGE_APP_STORE_BUTTON,
        },
    },
} = LANGUAGE_KEYS;

const ICON_SIZE = {
    width: 145,
    height: 40,
};

const MARKET_LINK = `market://details?id=${ANDROID_APP_ID}`;
const WEB_MARKET_LINK = `https://play.google.com/store/apps/details?id=${ANDROID_APP_ID}`;

const WEB_APP_STORE_LINK = `https://apps.apple.com/us/app/${IOS_APP_NAME}/${IOS_APP_ID}`;

const LandingScreen = () => {
    const { bottom: bottomInset } = useDebouncedSafeAreaInsets();
    const { t } = useTranslation();

    const { background, primary } = useSelector(selectLayoutConfig);

    const isNativePlatform =
        (isMobileMediaQuery || isTabletMediaQuery) &&
        Number.isInteger(window.orientation);

    const [orientationAngle, setOrientationAngle] = useState(
        window.matchMedia('(orientation: portrait)').matches ? 0 : 90,
    );
    const [platform, setPlatform] = useState<
        'Android' | 'iOS' | 'Desktop' | ''
    >('');

    const isLandscape = useMemo(
        () => isMobileMediaQuery && orientationAngle === 90,
        [orientationAngle],
    );

    const appLogoSize = isNativePlatform ? widthPercentageToDP(64, 80) : 96;

    const handleGooglePlayRedirect = useCallback(() => {
        if (isNativePlatform) {
            // @ts-ignore
            window.location = MARKET_LINK;
        } else {
            Linking.openURL(WEB_MARKET_LINK);
        }
    }, []);

    const timeoutRef = useRef<NodeJS.Timeout>(undefined);
    const windowStateChangeRef = useRef(false);

    const handleAppleStoreRedirect = useCallback(() => {
        Linking.openURL(WEB_APP_STORE_LINK);

        setTimeout(() => {
            window.location.reload();
            clearTimeout(timeoutRef.current);
        }, 10);
    }, []);

    const handleOpenDownloadPress = useCallback(async () => {
        const baseOS = await getBaseOs();

        const isIOS =
            baseOS === 'iOS' ||
            (baseOS !== 'Android' && !baseOS.includes('Linux'));

        try {
            const now = Date.now();

            window.location.replace(`web+fourig://${now}`);

            // Fallback function
            timeoutRef.current = setTimeout(() => {
                if (!windowStateChangeRef.current) {
                    if (isIOS) {
                        handleAppleStoreRedirect();
                    } else {
                        handleGooglePlayRedirect();
                    }
                }
            }, 500);
        } catch (e) {
            if (isIOS) {
                handleAppleStoreRedirect();
            } else {
                handleGooglePlayRedirect();
            }
        }
    }, [handleGooglePlayRedirect, handleAppleStoreRedirect]);

    const handleWebOrientationChange = useCallback((mediaQuery) => {
        if (mediaQuery.matches) {
            setOrientationAngle(0);
        } else {
            setOrientationAngle(90);
        }
    }, []);

    const handleAndroidHeightReset = useCallback(() => {
        if (platform === 'Android' && isMobileMediaQuery) {
            // After orientation change to landscape some browsers like Chrome address bar subtracts height from document height
            // Due to this issue, scroll behaviour breaks, it restores with height resetting
            document.body.style.height = `${window.screen.height}px`;
            document.getElementById('app-root')!.style.height = 'inherit';
        }
    }, [isLandscape, platform]);

    const handleIOSScrollDisable = useCallback(() => {
        if (platform === 'iOS') {
            if (background) {
                document.body.style.backgroundColor = background;
            }

            document.body.style.overflow = 'hidden';
        }
    }, [background, platform]);

    const handlePlatformSelect = useCallback(async () => {
        const baseOS = await getBaseOs();
        const isAndroid = baseOS === 'Android' || baseOS.includes('Linux');

        if (isAndroid) {
            setPlatform('Android');
        } else if (
            (!isAndroid && isMobileMediaQuery) ||
            (!isAndroid && isTabletMediaQuery)
        ) {
            setPlatform('iOS');
        } else {
            setPlatform('Desktop');
        }
    }, []);

    useEffect(() => {
        if (isNativePlatform) {
            navigator.registerProtocolHandler?.(
                'web+fourig',
                `${window.location.toString()}/?uri=%s`,
            );
        }
    }, []);

    useEffect(() => {
        handleIOSScrollDisable();
    }, [handleIOSScrollDisable]);

    useEffect(() => {
        window
            .matchMedia('(orientation: portrait)')
            .addListener(handleWebOrientationChange);

        return () =>
            window
                .matchMedia('(orientation: portrait)')
                .removeListener(handleWebOrientationChange);
    }, [handleWebOrientationChange]);

    useEffect(() => {
        handleAndroidHeightReset();
    }, [handleAndroidHeightReset]);

    useEffect(() => {
        handlePlatformSelect();
    }, [handlePlatformSelect]);

    useEffect(() => {
        window.addEventListener(
            'blur',
            () => {
                windowStateChangeRef.current = true;
            },
            false,
        );

        window.addEventListener(
            'focus',
            () => {
                windowStateChangeRef.current = false;
            },
            false,
        );
    }, []);

    return (
        <SafeAreaView
            style={[
                gStyles.container,
                styles.container,
                isLandscape && styles.containerLandscape,
                {
                    backgroundColor: background,
                    marginBottom: Math.max(
                        isMobileMediaQuery ? 24 : 0,
                        bottomInset,
                    ),
                },
            ]}
        >
            <View
                style={[
                    styles.logoContainer,
                    isLandscape && styles.logoContainerLandscape,
                ]}
                accessibilityRole="image"
                aria-label="OneTV logo"
                role="img"
                {...(isWeb && { tabIndex: 0 })}
                accessible
            >
                <AppLogo
                    testID={LOGO_TEST_ID}
                    width={appLogoSize}
                    height={appLogoSize}
                />
            </View>

            <Text
                category={ETextCategory.title1}
                style={styles.title}
                color={ETextColor.high_contrast}
                {...(isWeb && { tabIndex: 0 })}
                center
                bold
            >
                <LocalizedText textKey={LANDING_PAGE_MOBILE_BROWSER_TITLE} />
            </Text>
            <Text
                category={ETextCategory.body1}
                color={ETextColor.neutral}
                style={[styles.text, isLandscape && styles.textLandscape]}
                {...(isWeb && { tabIndex: 0 })}
                center
            >
                <LocalizedText textKey={LANDING_PAGE_MOBILE_BROWSER_BODY} />
            </Text>
            <View style={styles.downloadContainer}>
                <Text
                    category={ETextCategory.body1}
                    color={ETextColor.high_contrast}
                    {...(isWeb && { tabIndex: 0 })}
                    center
                >
                    <LocalizedText
                        textKey={LANDING_PAGE_MOBILE_BROWSER_STORE_LABEL}
                    />
                </Text>
                <View
                    style={[
                        gStyles.flexRow,
                        styles.btnContainer,
                        isLandscape && styles.btnContainerLandscape,
                    ]}
                >
                    <SvgButton
                        Svg={GooglePlayIcon}
                        style={[ICON_SIZE, styles.btnWithMargin]}
                        svgStyle={ICON_SIZE}
                        onPress={handleGooglePlayRedirect}
                        accessibilityLabel={t(
                            ACCESSIBILITY_LANDING_PAGE_GOOGLE_PLAY_BUTTON,
                        )}
                        accessibilityRole="button"
                        accessible
                    />
                    <SvgButton
                        Svg={AppStoreIcon}
                        style={[ICON_SIZE, styles.btnAppleMarket]}
                        svgStyle={ICON_SIZE}
                        onPress={handleAppleStoreRedirect}
                        accessibilityLabel={t(
                            ACCESSIBILITY_LANDING_PAGE_APP_STORE_BUTTON,
                        )}
                        accessibilityRole="button"
                        accessible
                    />
                </View>
                {isNativePlatform && (
                    <View
                        style={[
                            styles.separatorContainer,
                            isLandscape && styles.btnContainerLandscape,
                        ]}
                    >
                        <View style={styles.separator} />

                        <Text
                            category={ETextCategory.body2}
                            color={ETextColor.high_contrast}
                            style={[
                                styles.separatorText,
                                {
                                    backgroundColor: background,
                                },
                            ]}
                            {...(isWeb && { tabIndex: 0 })}
                            center
                        >
                            <LocalizedText
                                textKey={LANDING_PAGE_MOBILE_BROWSER_OR_LABEL}
                            />
                        </Text>
                    </View>
                )}
            </View>
            {isNativePlatform && (
                <ActionButton
                    title={LANDING_PAGE_MOBILE_BROWSER_OPEN_APP_BUTTON}
                    accessibilityLabel={t(
                        LANDING_PAGE_MOBILE_BROWSER_OPEN_APP_BUTTON,
                    )}
                    onPress={handleOpenDownloadPress}
                    buttonStyle={styles.primaryBtn}
                    fillColor={primary}
                    buttonTextStyle={styles.btnText}
                />
            )}
        </SafeAreaView>
    );
};

export default LandingScreen;
